import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter, useRoute } from 'vue-router'

/**
 * Composable compartido para la navegación.
 * Único origen de verdad para los elementos del menú y sus permisos.
 * Usado por MobileBottomNav, DesktopSidebar, Header, etc.
 */
export function useNavigation() {
  const authStore = useAuthStore()
  const router = useRouter()
  const route = useRoute()

  const navItems = computed(() => [
    { label: 'Inicio', icon: 'pi pi-home', route: 'home', permission: null },
    // El panel va el primero de los de negocio: es la sección de la dirección y
    // sin ella en la barra no había forma de llegar desde cualquier sitio.
    { label: 'Panel', icon: 'pi pi-chart-bar', route: 'dashboard', permission: 'dashboard.access' },
    // El panel de marketing va pegado al de dirección: son las dos pantallas de
    // números, y quien tiene uno suele querer el otro. Solo lo ve quien tiene el
    // permiso, así que el asesor ni se entera de que existe.
    { label: 'Marketing', icon: 'pi pi-megaphone', route: 'marketing', permission: 'marketing.access' },
    // `pi-receipt` y no `pi-file-text`: este último no existe en PrimeIcons 8 y
    // el icono salía en blanco. Se comprueba en `navigation.test.ts`.
    { label: 'Cotizaciones', icon: 'pi pi-receipt', route: 'quotes.index', permission: 'quotes.manage' },
    // En el menú se llama «Ventas» y no «Ventas Tienda»: la barra superior tiene
    // que alojar todas las secciones y el nombre largo empujaba a las últimas
    // fuera de la pantalla. El título de la pantalla sigue siendo «Ventas Tienda»,
    // que es donde sí cabe explicar de qué ventas se trata.
    { label: 'Ventas', icon: 'pi pi-shopping-bag', route: 'store-sales.index', permission: 'store-sales.manage' },
    { label: 'Clientes', icon: 'pi pi-users', route: 'customers.index', permission: 'customers.manage' },
    // Dos entradas y no una de «Catálogo»: productos y sets son pantallas
    // distintas, con filtros distintos. Una entrada que llevara a las dos
    // escondería que el catálogo se puede ver de dos maneras.
    { label: 'Productos', icon: 'pi pi-box', route: 'catalog.products.index', permission: 'catalog.view' },
    { label: 'Sets', icon: 'pi pi-clone', route: 'catalog.sets.index', permission: 'catalog.view' },
    { label: 'Seguimientos', icon: 'pi pi-calendar', route: 'followups.index', permission: 'followup.manage' },
    { label: 'Usuarios', icon: 'pi pi-user-plus', route: 'users.index', permission: 'users.manage' },
  ])

  const userMenuItems = computed(() => [
    {
      label: 'Mi perfil',
      icon: 'pi pi-user',
      command: () => router.push({ name: 'profile' }),
    },
    { separator: true },
    {
      label: 'Cerrar sesión',
      icon: 'pi pi-sign-out',
      command: () => authStore.logout().then(() => router.push({ name: 'login' })),
    },
  ])

  function canSee(item: { permission: string | null }): boolean {
    return !item.permission || authStore.hasPermission(item.permission)
  }

  function navigate(item: { route: string | null }): void {
    if (item.route) {
      router.push({ name: item.route })
    }
  }

  function isActive(routeName: string | null): boolean {
    if (!routeName) return false
    return route.name === routeName || route.matched.some(m => m.name === routeName)
  }

  return {
    navItems,
    userMenuItems,
    canSee,
    navigate,
    isActive,
  }
}